/**
 * @fileoverview Tipos para instalaciones de la Guardia Civil en Andalucía
 * extraídas del CSV de datos abiertos (direcciones_dependencias).
 *
 * Fuente: Guardia Civil — Datos reutilizables
 * Licencia: Datos abiertos reutilizables
 * Coordenadas: EPSG:25830 (geocodificadas y reproyectadas desde WGS84)
 *
 * @version 1.0.0
 * @date 2026-02-22
 */

/** Subtipos de instalación de la Guardia Civil */
export type GCFacilitySubtipo =
  | 'PUESTO' | 'COMPANIA' | 'COMANDANCIA' | 'SUBSECTOR' | 'OTROS';

/** Método de geocodificación utilizado */
export type GeocodingMethod = 'cartociudad' | 'nominatim' | 'manual';

/**
 * Feature de instalación Guardia Civil normalizada para R2.
 * Esquema compatible con el patrón R2Service del proyecto.
 */
export interface GCFacilityFeature {
  /** ID único: "gc-puesto-colomera" */
  id: string;
  /** Nombre completo: "PUESTO DE COLOMERA" */
  nombre: string;
  /** Tokens pre-computados para uFuzzy (lowercase, sin acentos) */
  tokens: string;
  /** Clasificación: PUESTO | COMPANIA | COMANDANCIA | SUBSECTOR | OTROS */
  subtipo: GCFacilitySubtipo;
  /** Nombre completo de la unidad */
  unidad: string;
  /** Domicilio original del CSV */
  direccion: string;
  /** Localidad */
  localidad: string;
  /** Código postal */
  codigoPostal: string;
  /** Código INE municipio (5 dígitos) — asignado por proximidad centroide */
  codMun: string;
  /** Nombre municipio */
  municipio: string;
  /** Provincia */
  provincia: string;
  /** Coordenada X UTM30 EPSG:25830 */
  x: number;
  /** Coordenada Y UTM30 EPSG:25830 */
  y: number;
  /** Fuente de datos */
  fuente: 'guardia_civil';
  /** Confianza geocodificación (0-100) */
  confianzaGeocodificacion: number;
  /** Método de geocodificación utilizado */
  metodoGeocodificacion: GeocodingMethod;
  /** Teléfono de contacto */
  telefono: string;
}

// =============================================================================
// OPCIONES Y RESULTADOS (R2 Service)
// =============================================================================

/** Opciones para búsqueda en GCFacilities R2 */
export interface GCFacilitySearchOptions {
  /** Código INE municipio (5 dígitos) — REQUERIDO */
  codINE: string;
  /** Nombre infraestructura para fuzzy match */
  name?: string;
  /** Filtrar por subtipo */
  subtipo?: GCFacilitySubtipo;
  /** Umbral mínimo confianza (0-100, default: 55) */
  minConfidence?: number;
  /** Máximo resultados (default: 5) */
  maxResults?: number;
}

/** Resultado de búsqueda GCFacilities R2 */
export interface GCFacilityResult {
  /** Coordenada X UTM30 */
  x: number;
  /** Coordenada Y UTM30 */
  y: number;
  /** Confianza (0-100) */
  confidence: number;
  /** Precisión estimada metros */
  precision: number;
  /** Identificador de fuente */
  source: 'gc_facilities_r2';
  /** Feature encontrada */
  feature: GCFacilityFeature;
  /** Score fuzzy (0-1) si aplica */
  fuzzyScore?: number;
  /** Nombre coincidente */
  matchedName: string;
}

// =============================================================================
// CONSTANTES VALIDACIÓN
// =============================================================================

/** Rango válido X UTM30 Andalucía */
export const GC_X_RANGE = { min: 100_000, max: 700_000 };
/** Rango válido Y UTM30 Andalucía */
export const GC_Y_RANGE = { min: 3_950_000, max: 4_300_000 };
/** Precisión estimada geocodificación CartoCiudad (metros) */
export const GC_PRECISION_CARTOCIUDAD_M = 20;
/** Precisión estimada geocodificación Nominatim (metros) */
export const GC_PRECISION_NOMINATIM_M = 50;

/** Entrada de caché de geocodificación */
export interface GeocodeCache {
  [key: string]: {
    lat: number;
    lon: number;
    method: GeocodingMethod;
    confidence: number;
    timestamp: string;
  };
}
